import {Injectable, Injector} from "@angular/core";
import {DX_CORE_CONFIG} from "../config/dx-core-config";
import {DxEncryptionStrategy} from "./strategy/dx-encryption-strategy";
import {DxBasicEncryptionStrategy} from "./strategy/dx-basic-encryption-strategy";
import {DxEncryptionStrategyType} from "../config/dx-encryptions-type";
import {DxAppError} from "../domain/error/dx-app-error";
import {DxKeycloakEncryptionStrategy} from "./strategy/dx-keycloak-encryption-strategy";


/**
 *   Серфис предоставляет функциональность шифрования\дешифрования данных.
 *   @publicApi
 */
@Injectable()
export class DxEncryptionService {
  /**
   * Текущая стратегия шифрования данных.
   * @name _encryptionStrategy
   * @type {_encryptionStrategy}
   * @defaultValue - инстанс DxBasicEncryptionStrategy
   */
  private _encryptionStrategy: DxEncryptionStrategy;

  /**
   * Созает инстанс сервиса.
   * @constructor
   * @param {Injector} _injector - инжектор.
   */
  constructor(private _injector: Injector) {
    const config = this._injector.get(DX_CORE_CONFIG);
    if(config) {
      this._encryptionStrategy = DxEncryptionService._dispatchEncryptionStrategy(this._injector, config.encryptionStrategyType);
    } else {
      this._encryptionStrategy = this._injector.get(DxBasicEncryptionStrategy);
    }
  }

  /**
   * Возвращает строку с данными, зашифрованными на основе текущей стратегии шифрования.
   * Тип стратегии шифрования может быть передан через параметр. Иначе будет использована
   * стратегия шифрования, определенная в конфигурации ядра.
   *
   * @param {any} data - объект данных произвольного типа, подлежащий зашифровке.
   * @param {DxEncryptionStrategyType} strategyType - тип статегии шифрования (опциональный параметр).
   * @return {string} - строка с зашифрованными данными.
   * @public
   */
  public encrypt(data: any, strategyType?: DxEncryptionStrategyType): string {
    if(strategyType) {
      const strategy = DxEncryptionService._dispatchEncryptionStrategy(this._injector, strategyType);
      return strategy.encrypt(data);
    }
    return this._encryptionStrategy.encrypt(data);
  }

  /**
   * Дешифрует данные на основе текущей стратегии шифрования.
   * Тип стратегии шифрования может быть передан через параметр. Иначе будет использована
   * стратегия шифрования, определенная в конфигурации ядра.
   *
   * @param {string} hash - строка с зашифрованными данными.
   * @param {DxEncryptionStrategyType} strategyType - тип статегии шифрования (опциональный параметр).
   * @return {any} - `JSON`-объек дешифрованных данных.
   * @public
   */
  public decode(hash: string, strategyType?: DxEncryptionStrategyType): any {
    if(strategyType) {
      const strategy = DxEncryptionService._dispatchEncryptionStrategy(this._injector, strategyType);
      return strategy.decode(hash);
    }
    return this._encryptionStrategy.decode(hash);
  }


  /**
   * Возвращает реалзацию стратегии ширования данных на основе переданного типа.
   * В случае передачи неподдерживаемого типа стратегии будет выброшено исключение.
   * @param {Injector} injector - инжектор.
   * @param {DxEncryptionStrategyType} type - тип стратегии шифрования данных.
   * @return {DxEncryptionStrategy} - инстанс требуемой стратегии шифрования.
   */
  private static _dispatchEncryptionStrategy(injector: Injector, type: DxEncryptionStrategyType): DxEncryptionStrategy {
    switch (type) {
      case DxEncryptionStrategyType.BASIC:
        return injector.get(DxBasicEncryptionStrategy);
      case DxEncryptionStrategyType.KEYCLOAK:
        return injector.get(DxKeycloakEncryptionStrategy);
      default:
        const error = new DxAppError();
        error.message = 'Передан неподдерживаемый тип стратегии шифрования данных!';
        throw error;
    }
  }
}
